import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';

import { getPlatformIconUrl } from 'src/utils/platform-icon';

import { confirmInkfrogImport, getInkfrogImportStatus } from 'src/redux/slices/listing';

import Iconify from 'src/components/iconify';

import InkfrogImportResults from './inkfrog-import-results';
import { FILE_IMPORT } from '../import-review/decisions';
import ImportReviewDialog from '../import-review/import-review-dialog';

const POLL_INTERVAL = 3000;

const PHASE_LABELS = {
  0: 'Starting your import…',
  5: 'Reading your file…',
  10: 'Preparing your listings…',
  20: 'Looking up your live eBay listings…',
  50: 'Matching your file with eBay…',
  60: 'Looking for listings you already have…',
  65: 'Adding your listings…',
  72: 'Adding your drafts…',
  85: 'Copying your photos…',
  95: 'Almost done…',
  100: 'Import complete',
};

function getPhaseLabel(progress) {
  const thresholds = Object.keys(PHASE_LABELS).map(Number).sort((a, b) => b - a);
  const match = thresholds.find(t => progress >= t);
  return match !== undefined ? PHASE_LABELS[match] : PHASE_LABELS[0];
}

export default function InkfrogImportProgress({ jobId, onClose }) {
  const [status, setStatus] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [importReview, setImportReview] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await getInkfrogImportStatus(jobId);
      if (response) {
        setStatus(response);
      }
    } catch (err) {
      console.error('Failed to fetch Inkfrog import status:', err);
    }
  }, [jobId]);

  // Poll for status updates
  useEffect(() => {
    if (!jobId) return undefined;

    fetchStatus();

    const interval = setInterval(() => {
      if (status?.status !== 'completed' && status?.status !== 'failed' && status?.status !== 'needs_selection') {
        fetchStatus();
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [jobId, fetchStatus, status?.status]);

  // Reset when jobId changes
  useEffect(() => {
    setIsDismissed(false);
    setShowResults(false);
    setImportReview(null);
  }, [jobId]);

  // Auto-show results when completed
  useEffect(() => {
    if (status?.status === 'completed' && status?.result?.success) {
      setShowResults(true);
    }
  }, [status]);

  // The import pauses only when it found rows that look like listings the seller already has, so a
  // review with nothing in it is not a review — an empty list would open a dialog with no question.
  useEffect(() => {
    if (status?.status === 'needs_selection' && status?.duplicates?.length > 0 && !importReview) {
      setImportReview({
        matches: status.duplicates,
        otherListings: status.noDuplicates || [],
        remainingSlots: status.remainingSlots || 0,
      });
    }
  }, [status, importReview]);

  const handleImportReviewConfirm = async (decisions, pickedListings) => {
    setConfirmLoading(true);
    try {
      const result = await confirmInkfrogImport(jobId, decisions, pickedListings);
      setStatus({ status: 'completed', result, progress: 100 });
      setImportReview(null);
      setShowResults(true);
    } catch (err) {
      console.error('Failed to confirm Inkfrog import:', err);
    } finally {
      setConfirmLoading(false);
    }
  };

  if (!jobId || isDismissed) return null;

  const isProcessing = status?.status === 'processing' || status?.status === 'pending';
  const isNeedsSelection = status?.status === 'needs_selection';
  const isCompleted = status?.status === 'completed';
  const isFailed = status?.status === 'failed';
  const progress = status?.progress || 0;

  const handleDismiss = () => {
    setIsDismissed(true);
    onClose?.();
  };

  return (
    <>
      <Slide direction="up" in mountOnEnter unmountOnExit>
        <Card
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
            width: 360,
            boxShadow: (theme) => theme.customShadows.z20,
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box component="img" src={getPlatformIconUrl('ebay')} alt="" sx={{ width: 20, height: 20 }} />
                <Typography variant="subtitle2">inkFrog import</Typography>
              </Stack>
              <IconButton size="small" onClick={handleDismiss}>
                <Iconify icon="eva:close-fill" width={18} />
              </IconButton>
            </Stack>

            {isProcessing && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {getPhaseLabel(progress)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: 6, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                  {progress}%
                </Typography>
              </>
            )}

            {isNeedsSelection && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Iconify icon="eva:alert-circle-fill" width={20} sx={{ color: 'warning.main' }} />
                <Typography variant="body2" color="warning.main">
                  {status.duplicates?.length} look{status.duplicates?.length === 1 ? 's' : ''} like {status.duplicates?.length === 1 ? 'a listing' : 'listings'} you already have
                </Typography>
              </Stack>
            )}

            {isCompleted && status?.result && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Iconify icon="eva:checkmark-circle-2-fill" width={20} sx={{ color: 'success.main' }} />
                  <Typography variant="body2" color="success.main">
                    Import complete
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {status.result.totalImported || 0} listing{status.result.totalImported === 1 ? '' : 's'} imported
                  {status.result.conflicts?.length > 0 && ` (${status.result.conflicts.length} conflicts)`}
                </Typography>
                <Box
                  onClick={() => setShowResults(true)}
                  sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                >
                  <Typography variant="caption">View details</Typography>
                </Box>
              </Stack>
            )}

            {isFailed && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Iconify icon="eva:close-circle-fill" width={20} sx={{ color: 'error.main' }} />
                <Typography variant="body2" color="error.main">
                  Couldn&apos;t finish the import. Try again.
                </Typography>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Slide>

      {importReview && (
        <ImportReviewDialog
          open
          onClose={() => {
            setImportReview(null);
            handleDismiss();
          }}
          onConfirm={handleImportReviewConfirm}
          loading={confirmLoading}
          matches={importReview.matches}
          otherListings={importReview.otherListings}
          platform="inkfrog"
          remainingSlots={importReview.remainingSlots}
          review={FILE_IMPORT}
        />
      )}

      {showResults && status?.result && (
        <InkfrogImportResults
          open={showResults}
          onClose={() => {
            setShowResults(false);
            handleDismiss();
          }}
          result={status.result}
        />
      )}
    </>
  );
}

InkfrogImportProgress.propTypes = {
  jobId: PropTypes.string,
  onClose: PropTypes.func,
};
